import { Capacitor } from '@capacitor/core';

import { DEFAULT_CREDIT_CHECKOUT_PLAN } from '../components/CreditSystem/creditCheckoutUtils';
import { getAppOrigin } from '../shared/seo/siteOrigin';
import { clearPaymentFlowFlags } from './paymentReturnBootstrap';

const CHECKOUT_RETURN_KEY = 'ggj_checkout_return_path';

function stashCheckoutReturnPath(): void {
  try {
    const current = window.location.pathname + window.location.search;
    if (current.startsWith('/checkout')) return;
    sessionStorage.setItem(CHECKOUT_RETURN_KEY, current);
  } catch {
    /* best-effort */
  }
}

/** Navigate to the dedicated credits checkout page (web). */

export function creditCheckoutUrl(planId?: string): string {
  const resolved = planId ?? DEFAULT_CREDIT_CHECKOUT_PLAN;
  return `/checkout?plan=${encodeURIComponent(resolved)}`;
}

/** Absolute checkout URL on the current app host (stays on localhost in dev). */
export function checkoutAbsoluteUrl(planId?: string): string {
  return `${getAppOrigin()}${creditCheckoutUrl(planId)}`;
}

/** Cashfree return URL — always the current browser tab (localhost stays local). */
export function checkoutReturnOrigin(): string {
  return getAppOrigin();
}

function spaNavigate(url: string): void {
  if (window.location.pathname + window.location.search === url) return;
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** SPA navigation to /checkout with optional plan query param. */
export function goToCreditCheckout(planId?: string): void {
  stashCheckoutReturnPath();
  spaNavigate(creditCheckoutUrl(planId));
}

/** Checkout back button — return to the page that opened checkout, else /pricing. */
export function goBackFromCheckout(): void {
  try {
    const stored = sessionStorage.getItem(CHECKOUT_RETURN_KEY);
    if (stored && !stored.startsWith('/checkout')) {
      sessionStorage.removeItem(CHECKOUT_RETURN_KEY);
      spaNavigate(stored);
      return;
    }
  } catch {
    /* best-effort */
  }
  goToPricing();
}

/** Full-page navigation to checkout — use after payment failure (no home flash). */
export function goToCheckoutAfterPaymentFailure(planId?: string): void {
  try {
    localStorage.setItem('payment_in_progress', 'true');
    localStorage.removeItem('payment_return_target');
  } catch {
    /* best-effort */
  }
  window.location.replace(checkoutAbsoluteUrl(planId));
}

export function goToPricing(): void {
  spaNavigate('/pricing');
}

/** Web: dedicated /checkout URL. Native: open in-app modal. */
export function openCreditPurchase(options: {
  planId?: string;
  openNativeModal: () => void;
}): void {
  if (Capacitor.isNativePlatform()) {
    options.openNativeModal();
    return;
  }
  goToCreditCheckout(options.planId);
}

/** Full-page navigation so PaymentSuccessPage runs verification reliably after Cashfree modal. */
export function goToPaymentSuccess(orderId: string): void {
  const url = `${getAppOrigin()}/payment-success?type=purchase&order_id=${encodeURIComponent(orderId)}`;
  window.location.replace(url);
}

/** After Cashfree subscription mandate authorization (recurring billing). */
export function goToSubscriptionSuccess(subscriptionId: string): void {
  const url = `${getAppOrigin()}/payment-success?subscription_id=${encodeURIComponent(subscriptionId)}&type=recurring`;
  window.location.replace(url);
}

/** Details shown on the dedicated payment-failure page. */
export interface PaymentFailedPayload {
  reason: string;
  planId?: string | null;
  orderId?: string | null;
  subscriptionId?: string | null;
  recurring?: boolean;
  elapsedSeconds?: number;
}

const PAYMENT_FAILED_KEY = 'ggj_payment_failed';

export function stashPaymentFailed(payload: PaymentFailedPayload): void {
  try {
    sessionStorage.setItem(PAYMENT_FAILED_KEY, JSON.stringify(payload));
  } catch {
    /* best-effort */
  }
}

export function readPaymentFailed(): PaymentFailedPayload | null {
  try {
    const raw = sessionStorage.getItem(PAYMENT_FAILED_KEY);
    return raw ? (JSON.parse(raw) as PaymentFailedPayload) : null;
  } catch {
    return null;
  }
}

export function clearPaymentFailed(): void {
  try {
    sessionStorage.removeItem(PAYMENT_FAILED_KEY);
  } catch {
    /* best-effort */
  }
}

/**
 * Redirect to the dedicated payment-failure page (full-page so the payment
 * guards settle and no home/agent view flashes). Stashes the reason + retry
 * plan so the failure page can render detail and offer a retry.
 */
export function goToPaymentFailed(payload: PaymentFailedPayload): void {
  stashPaymentFailed(payload);
  // Terminal — tear down all payment-return guards so AppRouter / bootstrap
  // cannot force another trip through /payment-success.
  clearPaymentFlowFlags();
  const params = new URLSearchParams({ failed: '1' });
  if (payload.recurring) {
    params.set('type', 'recurring');
    const subId = payload.subscriptionId?.trim() || payload.orderId?.trim();
    if (subId) params.set('subscription_id', subId);
  } else if (payload.orderId?.trim()) {
    params.set('type', 'purchase');
    params.set('order_id', payload.orderId.trim());
  }
  if (payload.planId) params.set('plan', payload.planId);
  window.location.replace(`${getAppOrigin()}/payment-failed?${params.toString()}`);
}
